import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  EmojiEvents as RankingIcon,
  PictureAsPdf as PdfIcon,
  SmartToy as AiIcon,
  Assessment as AssessmentIcon,
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  SportsTennis as TennisIcon,
  SportsSoccer as FootballIcon,
  Sports as AthleticsIcon,
  SportsHockey as IceHockeyIcon,
  School as LearningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { loadPlayerProfileForUser, buildFallbackPlayerProfile } from '@utils/coachPlayerProfile';
import { generateDashboardReportCardPDF } from '@utils/generateDashboardReportCardPDF';
import { generateRankingEvaluationReportPDF } from '@utils/generateRankingEvaluationReportPDF';
import { CORE_SPORTS, getDefaultRankingSubcategory, isShootingSport } from '@utils/sports';

const getGradeColor = (grade: string): string => {
  switch (grade) {
    case 'A+':
      return '#4caf50';
    case 'A':
      return '#2196f3';
    case 'B':
      return '#ff9800';
    case 'C':
    default:
      return '#f44336';
  }
};

async function safeJson<T = unknown>(response: Response) {
  const text = await response.text();
  if (!text.trim()) return { success: false, message: response.statusText || 'Empty response' };
  try {
    return JSON.parse(text) as { success: boolean; data?: T; message?: string; bySubcategory?: unknown };
  } catch {
    return { success: false, message: 'Invalid response' };
  }
}

interface PlayerSelfProgressSectionProps {
  portalMode?: PortalMode;
  title?: string;
}

const PlayerSelfProgressSection: React.FC<PlayerSelfProgressSectionProps> = ({
  portalMode = 'coach',
  title = 'My Learning Progress',
}) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);

  const [player, setPlayer] = useState<any>(null);
  const [loadingPlayer, setLoadingPlayer] = useState(true);
  const [rankingReports, setRankingReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [rankingScores, setRankingScores] = useState<Record<string, Record<string, any>>>({});
  const [rankingScoresBySubcategory, setRankingScoresBySubcategory] = useState<
    Array<{
      sport: string;
      subcategory: string;
      subcategoryLabel: string;
      'self-evaluation': any;
      'coach-academy': any;
      'practical-test': any;
    }>
  >([]);
  const [loadingRankingScores, setLoadingRankingScores] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      setLoadingPlayer(true);
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const profile = await loadPlayerProfileForUser(user.id, token, portalMode === 'coach', user);
        if (profile) {
          setPlayer({
            ...profile,
            userId: profile.userId || user.id,
            user: {
              id: profile.userId || user.id,
              firstName: profile.firstName ?? profile.user?.firstName,
              lastName: profile.lastName ?? profile.user?.lastName,
              email: profile.email ?? profile.user?.email ?? user.email,
              role: profile.role ?? profile.user?.role ?? user.role,
              isActive: profile.isActive ?? profile.user?.isActive ?? true,
            },
          });
        } else if (portalMode === 'coach') {
          setPlayer(buildFallbackPlayerProfile(user));
        } else {
          setPlayer(null);
        }
      } catch {
        if (portalMode === 'coach' && user) {
          setPlayer(buildFallbackPlayerProfile(user));
        } else {
          setPlayer(null);
        }
      } finally {
        setLoadingPlayer(false);
      }
    };
    load();
  }, [user?.id, portalMode]);

  const loadRankingScores = async (playerId: string) => {
    try {
      setLoadingRankingScores(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/ranking-evaluations/dashboard/${playerId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const data = await safeJson(response);
      if (response.ok && data.success && data.data) {
        setRankingScores(data.data as Record<string, Record<string, any>>);
        const bySubcategory = (data as any)?.bySubcategory;
        setRankingScoresBySubcategory(Array.isArray(bySubcategory) ? bySubcategory : []);
      }
    } catch (error) {
      console.error('Error loading ranking scores:', error);
    } finally {
      setLoadingRankingScores(false);
    }
  };

  const loadRankingReports = async (playerData: any) => {
    if (!playerData?.id) return;
    try {
      setLoadingReports(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const v2Res = await fetch(`/api/ranking-reports/player/${playerData.id}`, { headers });
      const v2Data = await v2Res.json().catch(() => null);

      if (v2Res.ok && v2Data?.success && Array.isArray(v2Data.data) && v2Data.data.length > 0) {
        const categoryLabels: Record<string, string> = {
          'self-evaluation': 'Self Evaluation',
          'coach-academy': 'Coach/Academy Evaluation',
          'practical-test': 'Board Exam',
        };
        setRankingReports(
          v2Data.data.map((r: any) => ({
            reportId: r.reportId,
            categoryId: r.categoryId,
            categoryLabel: categoryLabels[r.categoryId] || r.categoryId,
            score: Number(r.score || 0),
            totalStrokes: Number(r.totalStrokes || r.ratedStrokes || 0),
            maxScore: Number(r.maxScore || 0),
            percentage: String(r.percentage || '0.0'),
            grade: String(r.grade || 'C'),
            gradeDescription: String(r.gradeDescription || ''),
            evaluationDate: r.periodEndDate || null,
            periodKey: r.periodKey,
            hasData: true,
            sport: r.sport || playerData.primarySport || 'Badminton',
            isPrimarySport: (r.sport || playerData.primarySport) === playerData.primarySport,
            isSecondarySport: (r.sport || playerData.primarySport) === playerData.secondarySport,
          }))
        );
        return;
      }

      const categories = ['self-evaluation', 'coach-academy', 'practical-test'];
      const reports: any[] = [];
      for (const categoryId of categories) {
        const response = await fetch(
          `/api/ranking-evaluations/summary/${categoryId}/player/${playerData.id}`,
          { headers }
        );
        const data = await safeJson(response);
        if (response.ok && data.success && data.data && data.data.totalStrokes > 0) {
          reports.push({
            categoryId,
            categoryLabel:
              categoryId === 'self-evaluation'
                ? 'Self Evaluation'
                : categoryId === 'coach-academy'
                  ? 'Coach/Academy Evaluation'
                  : 'Board Exam',
            score: data.data.score || 0,
            totalStrokes: data.data.totalStrokes || 0,
            maxScore: data.data.maxScore || 0,
            percentage: data.data.percentage || '0.0',
            grade: data.data.grade || 'C',
            gradeDescription: data.data.gradeDescription || '',
            evaluationDate: data.data.evaluationDate || null,
            hasData: true,
            sport: playerData.primarySport || 'Badminton',
            isPrimarySport: true,
          });
        }
      }
      setRankingReports(reports);
    } catch (error) {
      console.error('Error loading ranking reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (player?.id) {
      loadRankingScores(player.id);
      loadRankingReports(player);
    }
  }, [player?.id]);

  if (loadingPlayer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!player?.id) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No player profile linked yet. Use Training Evaluation and Learn and Rank to get started as a player.
      </Alert>
    );
  }

  const getSportTheme = (sportName: string) => {
    switch (sportName?.toLowerCase()) {
      case 'badminton':
        return { main: '#1565c0', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 58%, #dbeafe 100%)' };
      case 'cricket':
        return { main: '#2e7d32', light: '#e8f5e9', border: '#a5d6a7', gradient: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 58%, #dcfce7 100%)' };
      case 'tennis':
        return { main: '#827717', light: '#f9fbe7', border: '#dce775', gradient: 'linear-gradient(135deg, #f9fbe7 0%, #ffffff 58%, #fef9c3 100%)' };
      case 'football':
        return { main: '#00897b', light: '#e0f2f1', border: '#80cbc4', gradient: 'linear-gradient(135deg, #e0f2f1 0%, #ffffff 58%, #ccfbf1 100%)' };
      case 'ice hockey':
        return { main: '#5e35b1', light: '#ede7f6', border: '#b39ddb', gradient: 'linear-gradient(135deg, #ede7f6 0%, #ffffff 58%, #e9d5ff 100%)' };
      case 'athletics':
        return { main: '#ef6c00', light: '#fff3e0', border: '#ffcc80', gradient: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 58%, #ffedd5 100%)' };
      default:
        return { main: '#1976d2', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 58%, #dbeafe 100%)' };
    }
  };

  const evalTheme: Record<'Self' | 'Coach' | 'EncycGames', { main: string; light: string; border: string; title: string }> = {
    Self: { main: '#2e7d32', light: '#ecfdf5', border: '#86efac', title: 'Self Score' },
    Coach: { main: '#6d28d9', light: '#f3e8ff', border: '#c4b5fd', title: 'Coach Score' },
    EncycGames: { main: '#d97706', light: '#fffbeb', border: '#fcd34d', title: 'EncycGames Score' },
  };

  const getSportIcon = (sportName: string) => {
    const theme = getSportTheme(sportName);
    const sx = { fontSize: 28, color: theme.main };
    switch (sportName?.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon sx={sx} />;
      case 'cricket':
        return <CricketIcon sx={sx} />;
      case 'tennis':
        return <TennisIcon sx={sx} />;
      case 'football':
        return <FootballIcon sx={sx} />;
      case 'ice hockey':
        return <IceHockeyIcon sx={sx} />;
      case 'athletics':
        return <AthleticsIcon sx={sx} />;
      default:
        return <AthleticsIcon sx={sx} />;
    }
  };

  const formatScore = (achieved: number, max: number) => (max > 0 ? `${achieved}/${max}` : `${achieved}`);
  const formatPeriod = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '–';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const evalLabels: Record<string, string> = {
    'self-evaluation': 'Self',
    'coach-academy': 'Coach',
    'practical-test': 'EncycGames',
  };

  const normalizeCategoryLabel = (sport: string, raw?: string | null) => {
    const value = String(raw || '').trim().toLowerCase();
    if (isShootingSport(sport)) {
      if (!value || value === 'strokes-shots' || value === 'strokes and shots') return 'Shooting';
    }
    return raw && String(raw).trim().length > 0 ? String(raw) : 'Strokes and Shots';
  };

  const inferDefaultRole = (sport: string) => {
    if (sport === 'Badminton') return 'Singles/Doubles';
    if (sport === 'Football') return 'Winger/Midfielder/Defender/Striker';
    if (sport === 'Ice Hockey') return 'Forward/Defense/Goalie';
    return 'General';
  };

  const buildRowsForSport = (sport: string) => {
    const subcategoryRows = rankingScoresBySubcategory.filter((r) => r.sport === sport);
    const rows: Array<{
      evalLabel: string;
      categoryLabel: string;
      roleLabel: string;
      levelLabel: string;
      gradeWithCategory: string;
      pts: string;
      periodLabel: string;
    }> = [];

    if (subcategoryRows.length > 0) {
      for (const row of subcategoryRows) {
        for (const key of ['self-evaluation', 'coach-academy', 'practical-test'] as const) {
          const cat = row[key];
          if (!cat?.hasData && !cat?.grade) continue;
          rows.push({
            evalLabel: evalLabels[key] || key,
            categoryLabel: normalizeCategoryLabel(sport, row.subcategoryLabel),
            roleLabel: String(cat?.roleLabel || inferDefaultRole(sport)),
            levelLabel: cat?.levelLabel || 'Level 1 - Beginner',
            gradeWithCategory: cat.grade ? `Grade ${cat.grade}` : '–',
            pts: `${formatScore(Number(cat?.totalScore ?? 0), Number(cat?.maxScore ?? 0))} pts`,
            periodLabel: formatPeriod(cat?.evaluationDate ?? null),
          });
        }
      }
    } else {
      const categories = rankingScores[sport];
      if (categories) {
        for (const key of ['self-evaluation', 'coach-academy', 'practical-test'] as const) {
          const cat = categories[key];
          if (!cat?.hasData && !cat?.grade) continue;
          rows.push({
            evalLabel: evalLabels[key] || key,
            categoryLabel: normalizeCategoryLabel(sport, null),
            roleLabel: String(cat?.roleLabel || inferDefaultRole(sport)),
            levelLabel: cat?.levelLabel || 'Level 1 - Beginner',
            gradeWithCategory: cat.grade ? `Grade ${cat.grade}` : '–',
            pts: `${formatScore(Number(cat?.totalScore ?? 0), Number(cat?.maxScore ?? 0))} pts`,
            periodLabel: formatPeriod(cat?.evaluationDate ?? null),
          });
        }
      }
    }
    return rows;
  };

  const gradeFromRow = (r: { gradeWithCategory: string }) => {
    const m = r.gradeWithCategory.match(/Grade\s+([A-Z+])\s*(?:\(|$)/i);
    return m ? m[1] : '';
  };

  const sportsToRender = Array.from(new Set([...CORE_SPORTS, ...Object.keys(rankingScores || {})]));

  const downloadSummaryPdf = async () => {
    const sports = sportsToRender.map((sport) => {
      const tableRows = buildRowsForSport(sport);
      const mapRows = (label: string) =>
        tableRows
          .filter((r) => r.evalLabel === label)
          .map((r) => ({
            categoryLabel: r.categoryLabel,
            roleLabel: r.roleLabel,
            periodLabel: r.periodLabel,
            levelLabel: r.levelLabel,
            gradeWithCategory: r.gradeWithCategory,
            pts: r.pts,
          }));
      return {
        sport,
        isPrimarySport: sport === player.primarySport,
        isSecondarySport: sport === player.secondarySport,
        self: mapRows('Self'),
        coach: mapRows('Coach'),
        encycgames: mapRows('EncycGames'),
      };
    });
    const playerName =
      `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim() ||
      user?.email ||
      'Player';
    await generateDashboardReportCardPDF({
      playerName,
      academyName: player?.academyName || 'N/A',
      sports,
    });
  };

  const handleViewReport = (report: any) => {
    if (report.reportId) {
      navigate(paths.rankingReport(report.reportId));
      return;
    }
    const params = new URLSearchParams();
    if (report.evaluationDate) {
      const raw = String(report.evaluationDate);
      let dateStr = raw;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const d = new Date(raw);
        dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      params.set('date', dateStr);
      params.set('sport', report.sport || player.primarySport || 'Badminton');
    }
    const sport = report.sport || player.primarySport || 'Badminton';
    const baseUrl = paths.rankingEvaluation(report.categoryId, {
      sport,
      level: 'beginner',
      subcategory: getDefaultRankingSubcategory(sport),
    });
    const query = params.toString();
    navigate(query ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${query}` : baseUrl);
  };

  const formatReportPeriod = (evaluationDate: string | null) => {
    if (!evaluationDate) return 'N/A';
    const evalDate = new Date(evaluationDate);
    const year = evalDate.getFullYear();
    const month = evalDate.getMonth() + 1;
    const day = evalDate.getDate();
    if (month === 12 && day === 31) return `Yearly ${year}`;
    if (month === 6 && day === 30) return `H1 ${year}`;
    if (month === 12 && day === 30) return `H2 ${year}`;
    if (month <= 3) return `Q1 ${year}`;
    if (month <= 6) return `Q2 ${year}`;
    if (month <= 9) return `Q3 ${year}`;
    return `Q4 ${year}`;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f4c81' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<RankingIcon />} onClick={() => navigate(paths.rankingOverview)}>
            Learn and Rank
          </Button>
          <Button size="small" variant="outlined" startIcon={<AssessmentIcon />} onClick={() => navigate(paths.performanceDefault)}>
            Training Evaluation
          </Button>
          <Button size="small" variant="outlined" startIcon={<LearningIcon />} onClick={() => navigate(paths.reports)}>
            Reports
          </Button>
          <Button size="small" variant="contained" startIcon={<AiIcon />} onClick={() => navigate(paths.recommendations)}>
            AI Assistant
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 4, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #dbe8f6', boxShadow: '0 16px 40px rgba(15, 76, 129, 0.08)' }}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f4c81', lineHeight: 1.15 }}>
              Performance Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Compare self, coach, and EncycGames evaluations across every sport.
            </Typography>
          </Box>

          {loadingRankingScores ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="outlined" size="small" startIcon={<PdfIcon />} onClick={downloadSummaryPdf}>
                    Download Report Card
                  </Button>
                </Box>
              </Grid>
              {sportsToRender.map((sport) => {
                const hasData =
                  Object.values(rankingScores[sport] || {}).some((c: any) => c?.hasData || c?.grade) ||
                  buildRowsForSport(sport).length > 0;
                const tableRows = buildRowsForSport(sport);
                const sportTheme = getSportTheme(sport);
                const rowsByType = {
                  Self: tableRows.filter((r) => r.evalLabel === 'Self'),
                  Coach: tableRows.filter((r) => r.evalLabel === 'Coach'),
                  EncycGames: tableRows.filter((r) => r.evalLabel === 'EncycGames'),
                };

                return (
                  <Grid item xs={12} key={sport}>
                    <Card
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3,
                        border: `1px solid ${sportTheme.border}`,
                        bgcolor: '#fff',
                        background: sportTheme.gradient,
                        boxShadow: hasData ? `0 14px 32px ${sportTheme.main}18` : 'none',
                        opacity: hasData ? 1 : 0.85,
                      }}
                    >
                      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2,
                                bgcolor: sportTheme.light,
                                border: `1px solid ${sportTheme.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {getSportIcon(sport)}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: sportTheme.main }}>
                              {sport}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {sport === player.primarySport && (
                              <Chip label="Primary" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 20 }} />
                            )}
                            {sport === player.secondarySport && (
                              <Chip label="Secondary" size="small" color="secondary" sx={{ fontSize: '0.7rem', height: 20 }} />
                            )}
                          </Box>
                        </Box>
                        <Grid container spacing={1.25}>
                          {(['Self', 'Coach', 'EncycGames'] as const).map((type) => {
                            const sectionTheme = evalTheme[type];
                            return (
                              <Grid item xs={12} md={4} key={`${sport}-${type}`}>
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1.25,
                                    borderRadius: 2,
                                    height: '100%',
                                    bgcolor: sectionTheme.light,
                                    borderColor: sectionTheme.border,
                                    borderTop: `5px solid ${sectionTheme.main}`,
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: sectionTheme.main, mb: 0.9 }}>
                                    {sectionTheme.title}
                                  </Typography>
                                  {rowsByType[type].length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                      No data yet
                                    </Typography>
                                  ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                      {rowsByType[type].map((r, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            border: `1px solid ${sectionTheme.border}`,
                                            borderLeft: `4px solid ${sectionTheme.main}`,
                                            borderRadius: 1.5,
                                            p: 0.9,
                                            bgcolor: '#fff',
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 900 }}>
                                            {r.categoryLabel}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                            {r.periodLabel} | {r.levelLabel}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                            <Chip
                                              size="small"
                                              label={r.gradeWithCategory}
                                              sx={{
                                                height: 22,
                                                fontSize: '0.68rem',
                                                fontWeight: 900,
                                                bgcolor: r.gradeWithCategory !== '–' ? `${getGradeColor(gradeFromRow(r))}18` : '#f1f5f9',
                                                color: r.gradeWithCategory !== '–' ? getGradeColor(gradeFromRow(r)) : 'text.secondary',
                                              }}
                                            />
                                            <Typography variant="caption" sx={{ fontWeight: 900, color: sectionTheme.main }}>
                                              {r.pts}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Ranking Reports
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" onClick={() => navigate(paths.reports)}>
                All Reports
              </Button>
              <Button variant="outlined" size="small" startIcon={<AiIcon />} onClick={() => navigate(paths.recommendations)}>
                AI Assistant
              </Button>
            </Box>
          </Box>
          {loadingReports ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : rankingReports.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Score (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rankingReports.map((report, index) => (
                    <TableRow key={`${report.sport}-${report.categoryId}-${index}`} hover>
                      <TableCell>
                        <Chip label={report.sport || player.primarySport || 'N/A'} size="small" color={report.isPrimarySport ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell>{report.categoryLabel}</TableCell>
                      <TableCell>{formatReportPeriod(report.evaluationDate)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: getGradeColor(report.grade || 'C') }}>
                          {report.grade || 'C'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.score || 0} pts ({report.percentage}%)
                        </Typography>
                      </TableCell>
                      <TableCell>{report.totalStrokes} strokes</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button variant="contained" size="small" startIcon={<RankingIcon />} onClick={() => handleViewReport(report)}>
                            View
                          </Button>
                          {report.hasData && report.evaluationDate && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PdfIcon />}
                              sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
                              onClick={async () => {
                                try {
                                  let dateStr = String(report.evaluationDate).split('T')[0].split(' ')[0].split('+')[0].split('Z')[0];
                                  const apiUrl = `/api/ranking-evaluations/category/${report.categoryId}/player/${player.id}?date=${encodeURIComponent(dateStr)}&sport=${encodeURIComponent(report.sport || player.primarySport || 'Badminton')}`;
                                  const response = await fetch(apiUrl);
                                  const data = await response.json();
                                  if (!data.success) {
                                    alert(data.message || 'Failed to fetch evaluation data');
                                    return;
                                  }
                                  const submittedData = (data.data || []).filter(
                                    (item: any) => item.submitted === true || item.submitted === 'true' || item.submitted === 1
                                  );
                                  if (submittedData.length === 0) {
                                    alert(`No submitted evaluation data found for ${dateStr}.`);
                                    return;
                                  }
                                  const strokeTypesResponse = await fetch('/api/admin/ranking-stroke-types');
                                  const strokeTypesData = await strokeTypesResponse.json();
                                  const orderedStrokeTypes = (strokeTypesData.data || [])
                                    .filter((st: any) => st.isActive !== false)
                                    .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
                                  const submittedRatingsMap = new Map<string, any>();
                                  submittedData
                                    .filter((stroke: any) => stroke.rating && stroke.rating > 0)
                                    .forEach((stroke: any) => submittedRatingsMap.set(stroke.code, stroke));
                                  const strokes = orderedStrokeTypes
                                    .map((strokeType: any) => {
                                      const submittedDataItem = submittedRatingsMap.get(strokeType.code);
                                      if (!submittedDataItem) return null;
                                      return {
                                        code: strokeType.code,
                                        courtPosition: strokeType.courtPosition || '',
                                        halfSide: strokeType.halfSide || '',
                                        strokeType: strokeType.strokeType || '',
                                        strokeAction: strokeType.strokeAction || '',
                                        shotType: strokeType.shotType || '',
                                        placement: strokeType.placement || '',
                                        rating: submittedDataItem.rating || 0,
                                      };
                                    })
                                    .filter(Boolean);
                                  if (strokes.length === 0) {
                                    alert('No rated strokes found.');
                                    return;
                                  }
                                  await generateRankingEvaluationReportPDF({
                                    player,
                                    categoryId: report.categoryId,
                                    categoryLabel: report.categoryLabel,
                                    sport: report.sport || player.primarySport || 'Badminton',
                                    evaluationDate: dateStr,
                                    strokes,
                                    totalScore: report.score || 0,
                                    maxScore: report.maxScore || 0,
                                    ratedStrokes: report.totalStrokes || 0,
                                    percentage: parseFloat(report.percentage || '0'),
                                    grade: report.grade,
                                    gradeDescription: report.gradeDescription || '',
                                    coachName: 'N/A',
                                    submittedAt: submittedData[0]?.createdAt || new Date().toISOString(),
                                  });
                                } catch (err) {
                                  console.error(err);
                                  alert('Failed to generate report PDF.');
                                }
                              }}
                            >
                              Download
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No ranking reports available yet. Start with Learn and Rank to build your progress.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PlayerSelfProgressSection;
